import type React from 'react';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { DiscoveryFeatureListView } from '@sdd-maturity/shared-types';
import { relativeTime } from '../lib/utils';
import { countRecommendations } from '../lib/findings';
import { scoreColor, levelLabel, normalizeLevel, levelBadgeClass } from '../lib/score';
import { buildFeaturePath } from '../lib/hierarchy-route';

type SortCol = 'feature' | 'score' | 'level' | 'recommendations' | 'lastActivity' | 'scanned';
type SortDir = 'asc' | 'desc';

const LEVEL_ORDINAL: Record<string, number> = { L1: 1, L2: 2, L3: 3, L4: 4, L5: 5 };

/** Count recommendations for a feature — the "Recommendations" column reads the
 * number of dimensions carrying a recommendation (see countRecommendations). */
function getRecommendationCount(feature: DiscoveryFeatureListView): number {
  return countRecommendations(feature.dimensions);
}

function SortHeader({ col, label, sortCol, sortDir, onSort, align = 'left' }: {
  col: SortCol; label: string; sortCol: SortCol; sortDir: SortDir;
  onSort: (c: SortCol) => void; align?: 'left' | 'right' | 'center';
}) {
  const active = sortCol === col;
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
  const ariaSortValue = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined;
  return (
    <th
      className={`sortable ${alignClass}`}
      onClick={() => onSort(col)}
      aria-sort={ariaSortValue}
      style={{
        color: active ? 'var(--genai-sem-text-primary)' : 'var(--genai-sem-text-secondary)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}{active ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  );
}

interface FeatureTableProps {
  features: DiscoveryFeatureListView[];
}

export function FeatureTable({ features }: FeatureTableProps) {
  const [sortCol, setSortCol] = useState<SortCol>('lastActivity');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  function handleSort(col: SortCol) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  const allFeatures = features.map(f => {
    const level = normalizeLevel(f.overallLevel ?? f.level);
    const teamNumber = String(f.teamNumber ?? '');
    const featureId = String(f.formattedId ?? '');
    const name = String(f.featureName ?? f.name ?? '');
    const score = Number(f.overallScore ?? 0);
    const scanned = String(f.scanTimestamp ?? f.timestamp ?? '');
    const recommendations = getRecommendationCount(f);
    const lastActivity = f.lastUpdateDate ?? '';
    const stale = f.stale === true;
    return { featureId, name, level, score, scanned, recommendations, teamNumber, lastActivity, stale };
  });

  type MappedFeature = (typeof allFeatures)[number];

  function compareFeatures(a: MappedFeature, b: MappedFeature): number {
    let r = 0;
    if (sortCol === 'feature') r = a.featureId.localeCompare(b.featureId);
    else if (sortCol === 'score') r = a.score - b.score;
    else if (sortCol === 'level') r = (LEVEL_ORDINAL[a.level] ?? 0) - (LEVEL_ORDINAL[b.level] ?? 0);
    else if (sortCol === 'recommendations') r = a.recommendations - b.recommendations;
    else if (sortCol === 'lastActivity') r = (new Date(a.lastActivity).getTime() || 0) - (new Date(b.lastActivity).getTime() || 0);
    else if (sortCol === 'scanned') r = a.scanned.localeCompare(b.scanned);
    return sortDir === 'asc' ? r : -r;
  }

  // Two-way split mirroring RepoTable/classifyRepos' active/other pattern —
  // Discovery has exactly one non-active status (STALE), not Delivery's
  // five-way classification, so there's no discriminator function to share.
  const active = useMemo(() => allFeatures.filter(f => !f.stale), [allFeatures]);
  const other = useMemo(() => allFeatures.filter(f => f.stale), [allFeatures]);

  const sortedActive = useMemo(() => [...active].sort(compareFeatures), [active, sortCol, sortDir]);
  const sortedOther = useMemo(() => [...other].sort(compareFeatures), [other, sortCol, sortDir]);

  const [otherExpanded, setOtherExpanded] = useState(false);

  function renderRow(f: MappedFeature, showStatus: boolean): React.JSX.Element {
    return (
      <tr key={f.featureId} data-testid={`feature-row-${f.featureId}`}>
        <td>
          <div className="stacked-cell">
            {/* Canonical leaf URL only (owning team in the path). Without a
                team there is no valid path, so the name renders as plain text. */}
            {f.teamNumber ? (
              <Link
                to={buildFeaturePath(f.teamNumber, f.featureId)}
                data-testid={`feature-link-${f.featureId}`}
                className="font-medium"
              >
                {f.name || f.featureId}
              </Link>
            ) : (
              <span data-testid={`feature-link-${f.featureId}`} className="font-medium">
                {f.name || f.featureId}
              </span>
            )}
            {f.name && (
              <span className="text-xs text-muted">{f.featureId}</span>
            )}
          </div>
        </td>
        {showStatus && (
          <td><span className="badge badge-warning">STALE</span></td>
        )}
        <td className="text-right text-base font-bold" style={{ color: scoreColor(f.score) }}>
          {f.score > 0 ? f.score : <span className="text-muted">—</span>}
        </td>
        <td className="text-center">
          {f.level ? <span className={levelBadgeClass(f.level)}>{levelLabel(f.level)}</span> : <span className="text-muted">—</span>}
        </td>
        <td className="text-right text-secondary" style={{ fontWeight: f.recommendations > 0 ? 'var(--genai-weight-semibold)' : 'var(--genai-weight-normal)' }}>
          {f.recommendations > 0 ? f.recommendations : '—'}
        </td>
        <td className="text-muted">
          {f.lastActivity ? relativeTime(f.lastActivity) : '—'}
        </td>
        <td className="text-muted">
          {f.scanned ? relativeTime(f.scanned) : '—'}
        </td>
      </tr>
    );
  }

  const makeHeaderRow = (showStatus: boolean): React.JSX.Element => (
    <tr>
      <SortHeader col="feature" label="Feature" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
      {showStatus && <th>Status</th>}
      <SortHeader col="score" label="Score" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
      <SortHeader col="level" label="Level" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="center" />
      <SortHeader col="recommendations" label="Recommendations" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
      <SortHeader col="lastActivity" label="Last Activity" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
      <SortHeader col="scanned" label="Last Scanned" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
    </tr>
  );
  const activeHeaderRow = makeHeaderRow(false);
  const otherHeaderRow = makeHeaderRow(true);
  const activeCols = 6;

  return (
    <div data-testid="feature-table">
      {/* Active features — the set the summary tiles above describe. Its own
          table/section, no Status column (every row is active). */}
      <section data-testid="section-active" style={{ marginBottom: 'var(--genai-space-lg)' }}>
        <div className="card card-flush" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="repo-table" aria-label="Discovery feature scores">
            <thead>{activeHeaderRow}</thead>
            <tbody>
              {sortedActive.length === 0 ? (
                <tr><td colSpan={activeCols} className="text-center text-secondary">No active features</td></tr>
              ) : (
                sortedActive.map(f => renderRow(f, false))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Other features — stale (outside the current scan window), in a
          separate collapsed section with its own Status column. */}
      {sortedOther.length > 0 && (
        <section data-testid="section-other">
          <button
            type="button"
            onClick={() => setOtherExpanded((prev) => !prev)}
            aria-expanded={otherExpanded}
            className="section-header"
            style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'flex', alignItems: 'center', gap: '6px', padding: 'var(--genai-space-xs) 0' }}
          >
            <span className="section-toggle">{otherExpanded ? '▾' : '▸'}</span>
            <span className="text-base font-semibold">Other features ({sortedOther.length})</span>
          </button>
          {otherExpanded && (
            <div className="card card-flush" style={{ padding: 0, overflow: 'hidden', marginTop: 'var(--genai-space-sm)' }}>
              <table className="repo-table" aria-label="Discovery feature scores — other">
                <thead>{otherHeaderRow}</thead>
                <tbody>{sortedOther.map(f => renderRow(f, true))}</tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
